package com.fireavert.react.yardi

import com.fireavert.administration.yardi_integration_page.yardi_data_import_section.client.presentation.YardiPropertyImportController
import com.fireavert.administration.yardi_integration_page.yardi_data_import_section.client.presentation.YardiPropertyImportViewModel
import com.fireavert.logging.Logger
import com.fireavert.react.shared.SearchableDropDownList
import com.fireavert.utilities.getKoinInstance
import com.fireavert.yardi.common_data.data_models.responses.UnitImportStatus
import com.fireavert.yardi.common_data.data_models.responses.YardiImportUnitDataResponse
import com.fireavert.yardi.shared.logic.data_models.YardiMiniCredentials
import emotion.css.ClassName
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.launch
import mui.material.*
import mui.material.Size
import mui.material.styles.TypographyVariant
import mui.system.sx
import react.*
import react.dom.html.ReactHTML.div
import web.cssom.*
import web.cssom.LineStyle.Companion.solid

external interface YardiPropertyDataImportModuleProps : Props {
    var yardiCredentials: List<YardiMiniCredentials>
}

val YardiPropertyDataImportModule = FC<YardiPropertyDataImportModuleProps> { props ->
    val logger = getKoinInstance<Logger>()
    val controller = getKoinInstance<YardiPropertyImportController>()
    val viewModel = getKoinInstance<YardiPropertyImportViewModel>()

    // Stepper state
    var activeStep by useState(0)

    // Step 1 states
    var selectedProperty by useState("")
    var selectedCredentials by useState("")
    var selectedYardiProperty by useState("")
    var propertyInstalledAcknowledged by useState(false)
    var credentialOptions: List<YardiMiniCredentials> by useState(props.yardiCredentials)
    var yardiPropertyOptions: List<String> by useState(emptyList())
    var propertyOptions: Map<String, Int?> by useState(emptyMap())
    var propertySearchTerm by useState("")
    
    // Convert lists to Map<String, Int?> format for SearchableDropDownList
    val sortedYardiPropertyOptions = yardiPropertyOptions.sorted()
    val yardiPropertyOptionsMap: Map<String, Int?> = sortedYardiPropertyOptions.associateWith { sortedYardiPropertyOptions.indexOf(it) }

    // Step 2 states
    var selectedParsingOption by useState("")
    var yardiUnitNames: List<String> by useState(emptyList())
    var dashboardUnitNames: List<String> by useState(emptyList())

    // Step 3 states
    var importInProgress by useState(false)
    var importProgress by useState(0f)
    var importStatus by useState("")
    var importStarted by useState(false)

    // General states
    var loading by useState(false)
    var error by useState("")
    var importResults by useState<com.fireavert.administration.yardi_integration_page.presentation.ImportResults?>(null)
    var yardiImportResponse by useState<YardiImportUnitDataResponse?>(null)

    val stepList = listOf(
        "Select Property",
        "Select Parsing Method",
        "Run Data Import",
        "Review Results"
    )

    val parsingOptions = listOf(
        "Auto-match by unit number",
        "Solomon",
        "Manual mapping",
        "Import all units"
    )

    useEffect(props.yardiCredentials) {
        credentialOptions = props.yardiCredentials
    }

    // Auto-select first property when properties load
    useEffect(propertyOptions) {
        if (selectedProperty.isEmpty() && propertyOptions.isNotEmpty()) {
            val firstProperty = propertyOptions.keys.first()
            selectedProperty = firstProperty
            logger.d("Auto-selected first property: $firstProperty")
        }
    }

    // Auto-select Yardi property when there's only one option
    useEffect(yardiPropertyOptions) {
        if (selectedYardiProperty.isEmpty() && yardiPropertyOptions.size == 1) {
            selectedYardiProperty = yardiPropertyOptions.first()
            logger.d("Auto-selected Yardi property: ${yardiPropertyOptions.first()}")
        }
    }

    // Auto-navigate to results when import completes
    useEffect(yardiImportResponse, importResults) {
        if ((yardiImportResponse != null || importResults != null) && activeStep == 2 && importStarted) {
            activeStep = 3
        }
    }


    // Effects
    useEffectOnceWithCleanup {
        val viewStateJob = viewModel.viewState.onEach { viewState ->
            loading = viewState.loading
            error = viewState.error
            yardiPropertyOptions = viewState.yardiPropertyList
            propertyOptions = viewState.propertyList
            yardiUnitNames = viewState.yardiUnitNames
            dashboardUnitNames = viewState.dashboardUnitNames
            importInProgress = viewState.importInProgress
            importProgress = viewState.importProgress
            importStatus = viewState.importStatus
            importResults = viewState.importResults
            yardiImportResponse = viewState.yardiImportResponse
        }.launchIn(viewModel.scope)

        val onLoadJob = viewModel.scope.launch { controller.onLoad() }

        onCleanup {
            viewStateJob.cancel()
            onLoadJob.cancel()
        }
    }

    val handleBack = {
        if (activeStep > 0) {
            activeStep -= 1
        }
    }

    val handleNext = {
        if (activeStep < stepList.size - 1) {
            activeStep += 1
        }
    }

    fun handleRefreshYardiProperties() {
        if (selectedCredentials.isNotEmpty()) {
            viewModel.scope.launch {
                try {
                    controller.refreshYardiProperties(selectedCredentials.toInt(), "200")
                } catch (e: Exception) {
                    logger.e("Error refreshing Yardi properties: ${e.message}", e)
                }
            }
        }
    }

    fun handleLoadYardiUnitNames() {
        if (selectedCredentials.isNotEmpty() && selectedYardiProperty.isNotEmpty()) {
            viewModel.scope.launch {
                try {
                    controller.loadYardiUnitNames(selectedCredentials.toInt(), "200")
                } catch (e: Exception) {
                    logger.e("Error loading Yardi unit names: ${e.message}", e)
                }
            }
        }
    }

    fun handleLoadDashboardUnitNames() {
        if (selectedProperty.isNotEmpty()) {
            val propertyId = propertyOptions[selectedProperty]
            if (propertyId != null) {
                viewModel.scope.launch {
                    try {
                        println("Loading dashboard unit names for property: $selectedProperty, ID: $propertyId")
                        controller.loadDashboardUnitNames(propertyId)
                    } catch (e: Exception) {
                        logger.e("Error loading dashboard unit names: ${e.message}", e)
                    }
                }
            } else {
                logger.w("Property ID not found for selected property: $selectedProperty")
            }
        }
    }

    fun handleStartImport() {
        val propertyId = propertyOptions[selectedProperty]
        if (propertyId != null) {
            importStarted = true
            viewModel.scope.launch {
                try {
                    controller.startDataImport(
                        propertyId = propertyId.toString(),
                        credentialsId = selectedCredentials.toInt(),
                        yardiPropertyId = "200",
                        parsingMethod = selectedParsingOption,
                        yardiUnitNames = yardiUnitNames,
                        dashboardUnitNames = dashboardUnitNames
                    )
                } catch (e: Exception) {
                    logger.e("Error starting data import: ${e.message}", e)
                    importStarted = false
                }
            }
        } else {
            logger.w("Property ID not found for selected property: $selectedProperty")
        }
    }

    // Validation functions
    val isStep1Valid = selectedProperty.isNotEmpty() &&
            selectedCredentials.isNotEmpty() &&
            selectedYardiProperty.isNotEmpty() &&
            propertyInstalledAcknowledged
    
    // Debug validation
    useEffect(selectedProperty, selectedCredentials, selectedYardiProperty, propertyInstalledAcknowledged, yardiPropertyOptions) {
        logger.d("Validation check - Property: '$selectedProperty', Credentials: '$selectedCredentials', YardiProperty: '$selectedYardiProperty', Acknowledged: $propertyInstalledAcknowledged, YardiOptions: ${yardiPropertyOptions.size}, Valid: $isStep1Valid")
    }

    val isStep2Valid = selectedParsingOption.isNotEmpty()

    Paper {
        elevation = 2
        sx { 
            width = 100.pct
            padding = 32.px
            marginTop = 24.px
            marginBottom = 24.px
            backgroundColor = Color("#ffffff")
            borderRadius = 12.px
            border = Border(1.px, LineStyle.solid, Color("#e0e0e0"))
        }

        // Module Title
        Typography {
            variant = TypographyVariant.h5
            sx { 
                marginBottom = 24.px
                fontWeight = FontWeight.bold
                textAlign = TextAlign.center
            }
            +"Property Data Import"
        }

        // Desktop Stepper
        Stepper {
            this.activeStep = activeStep
            alternativeLabel = true
            sx {
                marginBottom = 32.px
                media(MediaQuery("(max-width: 768px)")) {
                    display = None.none
                }
            }

            for (step in stepList) {
                Step {
                    key = step
                    StepLabel { +step }
                }
            }
        }

        // Mobile Stepper (only on mobile)
        MobileStepper {
            variant = MobileStepperVariant.dots
            steps = stepList.size
            position = MobileStepperPosition.static
            this.activeStep = activeStep

            sx {
                display = None.none
                media(MediaQuery("(max-width: 768px)")) {
                    display = Display.flex
                }
                marginBottom = 24.px
                justifyContent = JustifyContent.center
            }

            nextButton = Button.create {
                size = Size.small
                +"Next"
            }
            backButton = Button.create {
                size = Size.small
                +"Back"
            }
        }

        // Error Display with Retry
        if (error.isNotEmpty()) {
            Alert {
                sx { marginBottom = 16.px }
                action = if (activeStep == 2 && importStarted) {
                    Button.create {
                        color = ButtonColor.inherit
                        size = Size.small
                        onClick = {
                            // Clear error and retry
                            error = ""
                            importStarted = false
                            importInProgress = false
                            importProgress = 0f
                            importStatus = ""
                        }
                        +"Retry"
                    }
                } else null
                +error
            }
        }

        // Step Content
        Box {
            sx { minHeight = 400.px }

            when (activeStep) {
                0 -> { // Step 1: Select Property
                    div {
                        className = ClassName {
                            display = Display.flex
                            flexDirection = FlexDirection.column
                            gap = 24.px
                        }


                        // Property Selection with Search
                        SearchableDropDownList {
                            labelText = "Property"
                            optionMap = propertyOptions
                            onChange = { selectedPair ->
                                selectedProperty = selectedPair.second ?: ""
                                logger.d("Property selected: ${selectedPair.second}, ID: ${selectedPair.first}")
                            }
                            mobileVersion = false
                            disabled = propertyOptions.isEmpty()
                            defaultValue = if (selectedProperty.isNotEmpty()) propertyOptions[selectedProperty] else null
                        }

                        // Yardi Property Credentials with Refresh Button
                        div {
                            className = ClassName {
                                display = Display.flex
                                gap = 8.px
                                alignItems = AlignItems.center
                            }

                            FormControl {
                                sx { flexGrow = number(1.0) }
                                InputLabel { +"Yardi Property Credentials" }
                                Select {
                                    value = selectedCredentials
                                    label = ReactNode("Yardi Property Credentials")
                                    onChange = { event, _ ->
                                        selectedCredentials = event.target.value as String
                                        selectedYardiProperty = ""
                                        yardiPropertyOptions = emptyList()
                                    }

                                    if (credentialOptions.isEmpty()) {
                                        MenuItem { disabled = true; +"Loading credentials..." }
                                    } else {
                                        credentialOptions.forEach { credential ->
                                            MenuItem {
                                                key = credential.id.toString()
                                                value = credential.id.toString()
                                                +credential.name
                                            }
                                        }
                                    }
                                }
                            }

                            Button {
                                variant = ButtonVariant.outlined
                                disabled = selectedCredentials.isEmpty() || loading
                                onClick = { handleRefreshYardiProperties() }
                                sx { minWidth = 100.px }
                                if (loading) {
                                    CircularProgress { size = 20.px }
                                } else {
                                    +"Refresh"
                                }
                            }
                        }

                        // Yardi Property List (populated after refresh)
                        FormControl {
                            fullWidth = true
                            sx { marginBottom = 16.px }
                            InputLabel { +"Yardi Property List" }
                            Select {
                                value = selectedYardiProperty
                                label = ReactNode("Yardi Property List")
                                disabled = yardiPropertyOptions.isEmpty()
                                onChange = { event, _ ->
                                    selectedYardiProperty = event.target.value as String
                                }

                                if (yardiPropertyOptions.isEmpty()) {
                                    MenuItem { disabled = true; +"Click refresh to load properties" }
                                } else {
                                    yardiPropertyOptions.forEach { property ->
                                        MenuItem {
                                            key = property
                                            value = property
                                            +property
                                        }
                                    }
                                }
                            }
                        }

                        // Acknowledgment Checkbox
                        FormControlLabel {
                            control = Checkbox.create {
                                checked = propertyInstalledAcknowledged
                                onChange = { _, checked ->
                                    propertyInstalledAcknowledged = checked
                                }
                            }
                            label = ReactNode("I acknowledge that this property has been completely installed and is ready for data import")
                        }
                    }
                }

                1 -> { // Step 2: Select Parsing Method
                    div {
                        className = ClassName {
                            display = Display.flex
                            flexDirection = FlexDirection.column
                            gap = 24.px
                        }


                        // Parsing Options Dropdown
                        FormControl {
                            fullWidth = true
                            sx { marginBottom = 24.px }
                            InputLabel { +"Parsing Options" }
                            Select {
                                value = selectedParsingOption
                                label = ReactNode("Parsing Options")
                                onChange = { event, _ ->
                                    selectedParsingOption = event.target.value as String
                                }

                                parsingOptions.forEach { option ->
                                    MenuItem {
                                        key = option
                                        value = option
                                        +option
                                    }
                                }
                            }
                        }

                        // Unit Names Section
                        div {
                            className = ClassName {
                                display = Display.grid
                                gridTemplateColumns = "1fr 1fr".unsafeCast<GridTemplateTracks>()
                                gap = 24.px
                                media(MediaQuery("(max-width: 768px)")) {
                                    gridTemplateColumns = "1fr".unsafeCast<GridTemplateTracks>()
                                }
                            }

                            // Yardi Unit Names
                            div {
                                className = ClassName {
                                    display = Display.flex
                                    flexDirection = FlexDirection.column
                                    gap = 8.px
                                }

                                Button {
                                    variant = ButtonVariant.contained
                                    disabled = selectedCredentials.isEmpty() || selectedYardiProperty.isEmpty() || loading
                                    onClick = { handleLoadYardiUnitNames() }
                                    sx { marginBottom = 8.px }
                                    +"Load Yardi Property Unit Names"
                                }

                                Paper {
                                    sx {
                                        height = 200.px
                                        overflow = Auto.auto
                                        padding = 8.px
                                        border = Border(1.px, solid, Color("#e0e0e0"))
                                    }

                                    if (yardiUnitNames.isEmpty()) {
                                        Typography {
                                            sx {
                                                textAlign = TextAlign.center
                                                color = Color("#666")
                                                fontStyle = FontStyle.italic
                                            }
                                            +"Click button above to load unit names"
                                        }
                                    } else {
                                        yardiUnitNames.forEach { unitName ->
                                            Typography {
                                                key = unitName
                                                sx {
                                                    padding = 4.px
                                                    borderBottom = Border(1.px, solid, Color("#e0e0e0"))
                                                }
                                                +unitName
                                            }
                                        }
                                    }
                                }
                            }

                            // Dashboard Unit Names
                            div {
                                className = ClassName {
                                    display = Display.flex
                                    flexDirection = FlexDirection.column
                                    gap = 8.px
                                }

                                Button {
                                    variant = ButtonVariant.contained
                                    disabled = selectedProperty.isEmpty() || loading
                                    onClick = { handleLoadDashboardUnitNames() }
                                    sx { marginBottom = 8.px }
                                    +"Load Dashboard Unit Names"
                                }

                                Paper {
                                    sx {
                                        height = 200.px
                                        overflow = Auto.auto
                                        padding = 8.px
                                        border = Border(1.px, solid, Color("#e0e0e0"))
                                    }

                                    if (dashboardUnitNames.isEmpty()) {
                                        Typography {
                                            sx {
                                                textAlign = TextAlign.center
                                                color = Color("#666")
                                                fontStyle = FontStyle.italic
                                            }
                                            +"Click button above to load unit names"
                                        }
                                    } else {
                                        dashboardUnitNames.forEach { unitName ->
                                            Typography {
                                                key = unitName
                                                sx {
                                                    padding = 4.px
                                                    borderTop = Border(1.px, solid, Color("#f0f0f0"))
                                                }
                                                +unitName
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                2 -> { // Step 3: Run Data Import
                    div {
                        className = ClassName {
                            display = Display.flex
                            flexDirection = FlexDirection.column
                            gap = 24.px
                            alignItems = AlignItems.center
                        }

                        if (!importInProgress && !importStarted) {
                            Typography {
                                variant = TypographyVariant.h6
                                +"Ready to import data with the following settings:"
                            }

                            // Summary of selections
                            Paper {
                                sx { 
                                    padding = 24.px
                                    width = 100.pct
                                    backgroundColor = Color("#f5f5f5")
                                }
                                div {
                                    className = ClassName { 
                                        display = Display.flex
                                        flexDirection = FlexDirection.column
                                        gap = 16.px
                                    }
                                    Typography { 
                                        sx { fontWeight = FontWeight.bold }
                                        +"Property: $selectedProperty" 
                                    }
                                    Typography { 
                                        sx { fontWeight = FontWeight.bold }
                                        +"Parsing Method: $selectedParsingOption" 
                                    }
                                    Typography { 
                                        sx { fontWeight = FontWeight.bold }
                                        +"Units Found: ${yardiUnitNames.size} Yardi units, ${dashboardUnitNames.size} Dashboard units" 
                                    }
                                }
                            }

                            Button {
                                onClick = { handleStartImport() }
                                variant = ButtonVariant.contained
                                size = Size.large
                                disabled = importStarted || importInProgress
                                sx {
                                    minWidth = 200.px
                                    height = 48.px
                                }
                                +"Start Import"
                            }
                        } else {
                            // Import in progress
                            Typography {
                                variant = TypographyVariant.h6
                                sx { marginBottom = 24.px }
                                +"Import in Progress"
                            }

                            CircularProgress {
                                size = 60.px
                                sx { marginBottom = 16.px }
                            }

                            if (importStatus.isNotEmpty()) {
                                Typography {
                                    variant = TypographyVariant.body1
                                    sx { 
                                        textAlign = TextAlign.center
                                        marginBottom = 16.px
                                    }
                                    +importStatus
                                }
                            }

                            LinearProgress {
                                variant = LinearProgressVariant.determinate
                                value = importProgress * 100
                                sx { 
                                    width = 100.pct
                                    height = 8.px
                                    borderRadius = 4.px
                                }
                            }

                            Typography {
                                variant = TypographyVariant.caption
                                sx { 
                                    marginTop = 8.px
                                    color = Color("#666")
                                }
                                +"${(importProgress * 100).toInt()}% Complete"
                            }
                        }
                    }
                }

                3 -> { // Step 4: Review Results
                    div {
                        className = ClassName {
                            display = Display.flex
                            flexDirection = FlexDirection.column
                            gap = 24.px
                        }

                        if (yardiImportResponse != null) {
                            // Overall Status
                            Alert {
                                sx { marginBottom = 16.px }
                                AlertTitle { 
                                    if (yardiImportResponse!!.success) {
                                        +"Import Completed Successfully"
                                    } else {
                                        +"Import Completed with Issues"
                                    }
                                }
                                +if (yardiImportResponse!!.generalErrors.isEmpty()) {
                                    "All units were processed."
                                } else {
                                    yardiImportResponse!!.generalErrors.joinToString("; ")
                                }
                            }

                            // Retry button for failed imports
                            if (!yardiImportResponse!!.success || yardiImportResponse!!.unitsFailedToImport > 0) {
                                Box {
                                    sx {
                                        display = Display.flex
                                        justifyContent = JustifyContent.center
                                        marginBottom = 24.px
                                    }
                                    Button {
                                        variant = ButtonVariant.contained
                                        color = ButtonColor.secondary
                                        onClick = {
                                            // Reset import state and go back to step 3
                                            importStarted = false
                                            importInProgress = false
                                            importProgress = 0f
                                            importStatus = ""
                                            // Clear previous results to start fresh
                                            yardiImportResponse = null
                                            importResults = null
                                            activeStep = 2
                                        }
                                        +"Retry Import"
                                    }
                                }
                            }

                            // Summary Statistics
                            Paper {
                                sx { 
                                    padding = 24.px
                                    marginBottom = 24.px
                                    backgroundColor = Color("#f8f9fa")
                                }
                                
                                Typography {
                                    variant = TypographyVariant.h6
                                    sx { marginBottom = 16.px }
                                    +"Import Summary"
                                }

                                div {
                                    className = ClassName {
                                        display = Display.grid
                                        gridTemplateColumns = "repeat(auto-fit, minmax(200px, 1fr))".unsafeCast<GridTemplateTracks>()
                                        gap = 16.px
                                    }

                                    // Total Units
                                    Paper {
                                        elevation = 0
                                        sx { 
                                            padding = 16.px
                                            textAlign = TextAlign.center
                                            backgroundColor = Color("#fff")
                                            border = Border(1.px, solid, Color("#e0e0e0"))
                                        }
                                        Typography {
                                            variant = TypographyVariant.h4
                                            sx { color = Color("#1976d2") }
                                            +"${yardiImportResponse!!.totalUnitsAttempted}"
                                        }
                                        Typography {
                                            variant = TypographyVariant.body2
                                            sx { color = Color("#666") }
                                            +"Total Units"
                                        }
                                    }

                                    // Successful
                                    Paper {
                                        elevation = 0
                                        sx { 
                                            padding = 16.px
                                            textAlign = TextAlign.center
                                            backgroundColor = Color("#fff")
                                            border = Border(1.px, solid, Color("#4caf50"))
                                        }
                                        Typography {
                                            variant = TypographyVariant.h4
                                            sx { color = Color("#4caf50") }
                                            +"${yardiImportResponse!!.unitsSuccessfullyImported}"
                                        }
                                        Typography {
                                            variant = TypographyVariant.body2
                                            sx { color = Color("#666") }
                                            +"Successful"
                                        }
                                    }

                                    // Failed
                                    Paper {
                                        elevation = 0
                                        sx { 
                                            padding = 16.px
                                            textAlign = TextAlign.center
                                            backgroundColor = Color("#fff")
                                            border = Border(1.px, solid, Color("#f44336"))
                                        }
                                        Typography {
                                            variant = TypographyVariant.h4
                                            sx { color = Color("#f44336") }
                                            +"${yardiImportResponse!!.unitsFailedToImport}"
                                        }
                                        Typography {
                                            variant = TypographyVariant.body2
                                            sx { color = Color("#666") }
                                            +"Failed"
                                        }
                                    }

                                    // Skipped
                                    Paper {
                                        elevation = 0
                                        sx { 
                                            padding = 16.px
                                            textAlign = TextAlign.center
                                            backgroundColor = Color("#fff")
                                            border = Border(1.px, solid, Color("#ff9800"))
                                        }
                                        Typography {
                                            variant = TypographyVariant.h4
                                            sx { color = Color("#ff9800") }
                                            +"${yardiImportResponse!!.unitsSkipped}"
                                        }
                                        Typography {
                                            variant = TypographyVariant.body2
                                            sx { color = Color("#666") }
                                            +"Skipped"
                                        }
                                    }
                                }
                            }

                            // Detailed Summary
                            Paper {
                                sx { 
                                    padding = 24.px
                                    marginBottom = 24.px
                                }
                                
                                Typography {
                                    variant = TypographyVariant.h6
                                    sx { marginBottom = 16.px }
                                    +"Detailed Breakdown"
                                }

                                val summary = yardiImportResponse!!.summary
                                
                                div {
                                    className = ClassName {
                                        display = Display.flex
                                        flexDirection = FlexDirection.column
                                        gap = 8.px
                                    }

                                    if (summary.newTenantsCreated > 0) {
                                        Typography {
                                            sx { color = Color("#4caf50") }
                                            +"✓ ${summary.newTenantsCreated} new tenants created"
                                        }
                                    }
                                    
                                    if (summary.unitsWithNoMapping > 0) {
                                        Typography {
                                            sx { color = Color("#ff9800") }
                                            +"⚠ ${summary.unitsWithNoMapping} units had no mapping in the import configuration"
                                        }
                                    }
                                    
                                    if (summary.unitsWithMissingConnectUnit > 0) {
                                        Typography {
                                            sx { color = Color("#ff9800") }
                                            +"⚠ ${summary.unitsWithMissingConnectUnit} units could not be found in the dashboard"
                                        }
                                    }
                                    
                                    if (summary.unitsWithNoCurrentResident > 0) {
                                        Typography {
                                            sx { color = Color("#ff9800") }
                                            +"⚠ ${summary.unitsWithNoCurrentResident} units had no current resident in Yardi"
                                        }
                                    }
                                    
                                    if (summary.unitsWithDatabaseErrors > 0) {
                                        Typography {
                                            sx { color = Color("#f44336") }
                                            +"✗ ${summary.unitsWithDatabaseErrors} units failed due to database errors"
                                        }
                                    }
                                    
                                    if (summary.unitsWithTenantCreationErrors > 0) {
                                        Typography {
                                            sx { color = Color("#f44336") }
                                            +"✗ ${summary.unitsWithTenantCreationErrors} units failed during tenant creation"
                                        }
                                    }
                                }
                            }

                            // Unit Details (if there are any issues)
                            if (yardiImportResponse!!.unitResults.any { it.status != UnitImportStatus.SUCCESS }) {
                                Paper {
                                    sx { 
                                        padding = 24.px
                                        maxHeight = 400.px
                                        overflow = Auto.auto
                                    }
                                    
                                    Typography {
                                        variant = TypographyVariant.h6
                                        sx { marginBottom = 16.px }
                                        +"Unit Import Details"
                                    }

                                    div {
                                        className = ClassName {
                                            display = Display.flex
                                            flexDirection = FlexDirection.column
                                            gap = 12.px
                                        }

                                        yardiImportResponse!!.unitResults
                                            .filter { it.status != UnitImportStatus.SUCCESS }
                                            .forEach { unitResult ->
                                                Paper {
                                                    elevation = 0
                                                    sx {
                                                        padding = 12.px
                                                        backgroundColor = when (unitResult.status) {
                                                            UnitImportStatus.FAILED_DATABASE_ERROR,
                                                            UnitImportStatus.FAILED_TENANT_CREATION,
                                                            UnitImportStatus.FAILED_OTHER -> Color("#ffebee")
                                                            else -> Color("#fff3e0")
                                                        }
                                                        border = Border(1.px, solid, when (unitResult.status) {
                                                            UnitImportStatus.FAILED_DATABASE_ERROR,
                                                            UnitImportStatus.FAILED_TENANT_CREATION,
                                                            UnitImportStatus.FAILED_OTHER -> Color("#f44336")
                                                            else -> Color("#ff9800")
                                                        })
                                                    }

                                                    Typography {
                                                        sx { 
                                                            fontWeight = FontWeight.bold
                                                            marginBottom = 4.px
                                                        }
                                                        +"Unit: ${unitResult.yardiUnitNumber ?: unitResult.yardiUnitId}"
                                                    }
                                                    
                                                    Typography {
                                                        variant = TypographyVariant.body2
                                                        sx { color = Color("#666") }
                                                        +when (unitResult.status) {
                                                            UnitImportStatus.SKIPPED_NO_MAPPING -> "Skipped: No mapping configured"
                                                            UnitImportStatus.SKIPPED_NO_CONNECT_UNIT -> "Skipped: Unit not found in dashboard"
                                                            UnitImportStatus.SKIPPED_NO_CURRENT_RESIDENT -> "Skipped: No current resident"
                                                            UnitImportStatus.FAILED_DATABASE_ERROR -> "Failed: Database error"
                                                            UnitImportStatus.FAILED_TENANT_CREATION -> "Failed: Could not create tenant"
                                                            UnitImportStatus.FAILED_OTHER -> "Failed: ${unitResult.reason}"
                                                            else -> unitResult.reason ?: ""
                                                        }
                                                    }
                                                    
                                                    if (!unitResult.errorMessage.isNullOrEmpty()) {
                                                        Typography {
                                                            variant = TypographyVariant.caption
                                                            sx { 
                                                                color = Color("#d32f2f")
                                                                marginTop = 4.px
                                                            }
                                                            +unitResult.errorMessage
                                                        }
                                                    }
                                                }
                                            }
                                    }
                                }
                            }
                        } else if (importResults != null) {
                            // Fallback to old importResults if new response not available
                            Typography {
                                variant = TypographyVariant.h6
                                +"Import completed!"
                            }
                            Typography {
                                +"Total processed: ${importResults!!.totalUnitsProcessed}"
                            }
                            Typography {
                                +"Successful: ${importResults!!.successfulImports}"
                            }
                            Typography {
                                +"Failed: ${importResults!!.failedImports}"
                            }
                        } else {
                            Typography {
                                +"No import results available."
                            }
                        }
                    }
                }
            }
        }

        // Navigation Buttons
        Box {
            sx {
                display = Display.flex
                justifyContent = JustifyContent.spaceBetween
                marginTop = 32.px
                paddingTop = 16.px
                borderTop = Border(1.px, solid, Color("#e0e0e0"))
            }

            Button {
                disabled = activeStep == 0
                onClick = { handleBack() }
                +"Back"
            }

            Button {
                variant = ButtonVariant.contained
                disabled = when (activeStep) {
                    0 -> !isStep1Valid
                    1 -> !isStep2Valid
                    2 -> importInProgress || (!importStarted && importResults == null)
                    else -> false
                }
                onClick = { 
                    if (activeStep == 2 && importResults != null) {
                        // Move to results page after import completes
                        activeStep = 3
                    } else {
                        handleNext()
                    }
                }

                when {
                    activeStep == stepList.size - 1 -> +"Finish"
                    activeStep == 2 && importInProgress -> +"Import in Progress..."
                    activeStep == 2 && importResults != null -> +"View Results"
                    else -> +"Next"
                }
            }
        }
    }
}